import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, CalendarDays, ShieldCheck, Stethoscope } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;
const DARK = "bg-gradient-to-br from-[#3b0764] via-[#4c1d95] to-[#2e1065]";

const BENEFITS = [
  "Veja a plataforma funcionando com os seus próprios dados",
  "Tire dúvidas sobre telemedicina, Memed e recebimento no Pix",
  "Entenda como migrar seus pacientes sem dor de cabeça",
  "Sem compromisso — você decide depois da demonstração",
];

export default function AgendarApresentacao() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", specialty: "", city: "", preferred_time: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      return toast.error("Preencha nome, e-mail e WhatsApp.");
    }
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/leads`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error();
      setDone(true);
    } catch {
      toast.error("Não foi possível enviar agora. Tente novamente em instantes.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${DARK}`} data-testid="agendar-page">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-5 py-5 lg:px-8">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/veloclin-logo.png" alt="VeloClin" className="h-11 w-auto rounded-full bg-white p-1" />
        </Link>
        <Link to="/login" className="text-sm font-semibold text-white/80 hover:text-white">Entrar</Link>
      </header>

      <div className="mx-auto grid max-w-6xl items-start gap-10 px-5 py-8 lg:grid-cols-2 lg:px-8 lg:py-14">
        <div className="text-white">
          <span className="inline-flex items-center gap-2 rounded-full border border-lime-400/30 bg-lime-400/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-lime-300">
            <Stethoscope className="h-3.5 w-3.5" /> Demonstração para médicos de todo o Brasil
          </span>
          <h1 className="mt-5 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl">
            Agende sua apresentação <span className="text-lime-300">gratuita</span>
          </h1>
          <p className="mt-4 max-w-md text-base leading-relaxed text-violet-100/85">
            15 minutos no Google Meet para você ver como a VeloClin coloca a sua medicina particular no controle — com o dinheiro da consulta direto no seu Pix e sem comissão.
          </p>
          <ul className="mt-7 space-y-3">
            {BENEFITS.map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm text-violet-100/90">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-lime-300" /> {b}
              </li>
            ))}
          </ul>
          <p className="mt-7 flex items-center gap-2 text-xs text-violet-100/70">
            <ShieldCheck className="h-4 w-4 text-lime-300" /> Migramos seus pacientes e histórico de graça em até 24h.
          </p>
        </div>

        <div className="rounded-3xl border border-white/10 bg-white p-7 shadow-2xl">
          {done ? (
            <div className="py-6 text-center" data-testid="agendar-success">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                <CheckCircle2 className="h-9 w-9 text-emerald-600" />
              </div>
              <h2 className="mt-5 font-heading text-2xl font-extrabold text-slate-900">Recebemos seu pedido!</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                Nossa equipe vai entrar em contato pelo seu WhatsApp <b>{form.phone}</b> para confirmar o melhor horário da sua apresentação. Fique de olho nas mensagens. 😊
              </p>
              <Link to="/" data-testid="agendar-back-home" className="mt-6 inline-flex items-center gap-2 rounded-full bg-violet-700 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-violet-800">
                Voltar ao site <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="agendar-form">
              <div className="flex items-center gap-2 text-slate-900">
                <CalendarDays className="h-5 w-5 text-violet-700" />
                <h2 className="font-heading text-lg font-extrabold">Quero agendar minha demonstração</h2>
              </div>
              <div className="mt-5 space-y-3.5">
                <div>
                  <label className="text-xs font-semibold text-slate-600">Nome completo *</label>
                  <Input data-testid="agendar-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" placeholder="Dr(a). Seu nome" />
                </div>
                <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-semibold text-slate-600">E-mail *</label>
                    <Input data-testid="agendar-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" placeholder="voce@email.com" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">WhatsApp *</label>
                    <Input data-testid="agendar-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" placeholder="(31) 9____-____" />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Especialidade</label>
                    <Input data-testid="agendar-specialty" value={form.specialty} onChange={(e) => set("specialty", e.target.value)} className="mt-1.5" placeholder="Ex.: Cardiologia" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Cidade / UF</label>
                    <Input data-testid="agendar-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1.5" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Melhor horário para a demonstração</label>
                  <Input data-testid="agendar-time" value={form.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} className="mt-1.5" placeholder="Ex.: terça à tarde, após as 18h..." />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Algo que você gostaria de resolver? (opcional)</label>
                  <Textarea data-testid="agendar-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} className="mt-1.5 resize-none" placeholder="Ex.: quero parar de depender de convênio e organizar minha agenda particular." />
                </div>
              </div>
              <button type="submit" data-testid="agendar-submit" disabled={loading} className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-lime-400 py-3.5 text-sm font-bold text-violet-950 transition-colors hover:bg-lime-300 disabled:opacity-60">
                {loading ? "Enviando..." : "Agendar apresentação"} <ArrowRight className="h-4 w-4" />
              </button>
              <p className="mt-3 text-center text-xs text-slate-400">Sem compromisso · resposta em até 1 dia útil</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
